import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useOrganizationScoping } from './useOrganizationScoping';

export interface ParcelType {
  id: string;
  key: string;
  title: string;
  description: string;
  icon_name: string;
  color_from: string;
  color_to: string;
  is_enabled: boolean;
  sort_order: number;
  default_weight?: string;
  default_dimensions?: {
    length?: string;
    width?: string;
    height?: string;
  };
  org_id?: string | null;
  created_at: string;
  updated_at: string;
}

export const useParcelTypes = (
  includeDisabled = false, 
  forPlatformAdmin = false, 
  orgSpecificOnly = false,
  selectedOrgId?: string | null
) => {
  const [parcelTypes, setParcelTypes] = useState<ParcelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organizationId, canAccessAllOrganizations } = useOrganizationScoping();

  const fetchParcelTypes = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('parcel_types')
        .select('*')
        .order('sort_order', { ascending: true });

      if (!includeDisabled) {
        query = query.eq('is_enabled', true);
      }

      // Use selectedOrgId if provided, otherwise use existing logic
      const targetOrgId = selectedOrgId !== undefined ? selectedOrgId : organizationId;

      // Filter based on context
      if (forPlatformAdmin && canAccessAllOrganizations) {
        if (selectedOrgId !== undefined) {
          // Platform admin with organization selector
          if (selectedOrgId === null) {
            // Show platform defaults
            query = query.is('org_id', null);
          } else {
            // Show specific organization's parcel types
            query = query.eq('org_id', selectedOrgId);
          }
        } else {
          // Platform admin sees only default/platform parcel types (org_id is null)
          query = query.is('org_id', null);
        }
      } else if (targetOrgId) {
        if (orgSpecificOnly) {
          // Show only organization-specific parcel types
          query = query.eq('org_id', targetOrgId);
        } else {
          // Organization users see their org's parcel types + platform defaults
          query = query.or(`org_id.eq.${targetOrgId},org_id.is.null`);
        }
      } else {
        // Fallback to platform defaults only
        query = query.is('org_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching parcel types:', error);
        setError(error.message);
        return;
      }

      // Transform the data to match our interface
      const transformedData: ParcelType[] = (data || []).map(item => ({
        id: item.id,
        key: item.key,
        title: item.title,
        description: item.description,
        icon_name: item.icon_name,
        color_from: item.color_from,
        color_to: item.color_to,
        is_enabled: item.is_enabled,
        sort_order: item.sort_order,
        default_weight: item.default_weight,
        default_dimensions: item.default_dimensions as { length?: string; width?: string; height?: string; } | undefined,
        org_id: item.org_id,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      // Deduplicate by key - prioritize organization-specific over platform defaults
      const deduplicatedData = transformedData.reduce((acc: ParcelType[], current) => {
        const existingIndex = acc.findIndex(item => item.key === current.key);
        if (existingIndex === -1) {
          // No duplicate found, add to result
          acc.push(current);
        } else {
          // Duplicate found - prioritize organization-specific (org_id not null) over platform defaults
          const existing = acc[existingIndex];
          if (current.org_id && !existing.org_id) {
            // Replace platform default with organization-specific
            acc[existingIndex] = current;
          }
          // If both are org-specific or both are platform defaults, keep the first one
        }
        return acc;
      }, []);

      setParcelTypes(deduplicatedData);
      setError(null);
    } catch (err) {
      console.error('Unexpected error fetching parcel types:', err);
      setError('Failed to load parcel types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParcelTypes();
  }, [includeDisabled, organizationId, forPlatformAdmin, canAccessAllOrganizations, orgSpecificOnly, selectedOrgId]);

  const createParcelType = async (parcelType: Omit<ParcelType, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => {
    try {
      // Determine the correct org_id based on context
      let orgId = null;
      if (forPlatformAdmin && canAccessAllOrganizations) {
        if (selectedOrgId !== undefined) {
          // Platform admin with organization selector - create for selected org
          orgId = selectedOrgId;
        } else {
          // Platform admin creating default parcel type
          orgId = null;
        }
      } else {
        orgId = organizationId; // Organization admin creating org-specific parcel type
      }

      const { error } = await supabase
        .from('parcel_types')
        .insert([{ ...parcelType, org_id: orgId }]);

      if (error) {
        toast.error('Failed to create parcel type');
        throw error;
      }

      toast.success('Parcel type created successfully');
      await fetchParcelTypes();
    } catch (error) {
      console.error('Error creating parcel type:', error);
      throw error;
    }
  };

  const updateParcelType = async (id: string, updates: Partial<ParcelType>, skipRefetch = false) => {
    try {
      const { error } = await supabase
        .from('parcel_types')
        .update(updates)
        .eq('id', id);

      if (error) {
        toast.error('Failed to update parcel type');
        throw error;
      }

      if (!skipRefetch) {
        toast.success('Parcel type updated successfully');
        await fetchParcelTypes();
      }
    } catch (error) {
      console.error('Error updating parcel type:', error);
      throw error;
    }
  };

  const batchUpdateSortOrder = async (updates: { id: string; sort_order: number }[]) => {
    try {
      // Update each item without refetching
      await Promise.all(
        updates.map(update => 
          updateParcelType(update.id, { sort_order: update.sort_order }, true)
        )
      );
      
      // Fetch once after all updates are complete
      await fetchParcelTypes();
      toast.success('Parcel types reordered successfully');
    } catch (error) {
      console.error('Error batch updating sort order:', error);
      toast.error('Failed to reorder parcel types');
      throw error;
    }
  };

  const deleteParcelType = async (id: string) => {
    try {
      const { error } = await supabase
        .from('parcel_types')
        .delete()
        .eq('id', id);

      if (error) {
        toast.error('Failed to delete parcel type');
        throw error;
      }

      toast.success('Parcel type deleted successfully');
      await fetchParcelTypes();
    } catch (error) {
      console.error('Error deleting parcel type:', error);
      throw error;
    }
  };

  return {
    parcelTypes,
    loading,
    error,
    refetch: fetchParcelTypes,
    createParcelType,
    updateParcelType,
    batchUpdateSortOrder,
    deleteParcelType,
  };
};